import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, GraduationCap, MapPin, FileText, Award, BookOpen } from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  College: GraduationCap, Course: BookOpen, City: MapPin, Exam: Award, Article: FileText,
};

const EXAMPLES = [
  "MBA colleges in Delhi", "PGDM colleges in Bangalore", "MBA colleges under 10 lakh",
  "CAT accepting colleges", "Best MBA colleges in Lucknow",
];

export function UniversalSearch({ variant = "light", autoFocus = false }) {
  const [q, setQ] = useState("");
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [ph, setPh] = useState(0);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  const dark = variant === "dark";

  useEffect(() => {
    const t = setInterval(() => setPh((p) => (p + 1) % EXAMPLES.length), 2600);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSug = useCallback(async (val) => {
    if (val.trim().length < 1) { setSugs([]); return; }
    try {
      const { data } = await api.get(`/autocomplete`, { params: { q: val } });
      setSugs(data.suggestions || []);
    } catch { setSugs([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchSug(q), 200);
    return () => clearTimeout(t);
  }, [q, fetchSug]);

  const submit = (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div ref={boxRef} className="relative w-full" data-testid="universal-search">
      <form onSubmit={submit}>
        <div className={`flex items-center gap-3 rounded-full px-5 py-3.5 shadow-xl ${dark ? "glass-dark" : "bg-white border border-black/5"}`}>
          <Search className={dark ? "text-gold" : "text-navy"} size={22} />
          <input
            data-testid="search-input"
            autoFocus={autoFocus}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={`Search ${EXAMPLES[ph]}...`}
            className={`flex-1 bg-transparent outline-none text-base ${dark ? "text-white placeholder:text-white/50" : "text-navy placeholder:text-muted-foreground"}`}
          />
          <button type="submit" data-testid="search-submit" className="gold-btn hidden sm:block rounded-full px-6 py-2 font-semibold text-sm">
            Search
          </button>
        </div>
      </form>

      {open && (q.trim().length > 0 ? sugs.length > 0 : true) && (
        <div data-testid="search-suggestions" className="absolute z-50 mt-2 w-full rounded-2xl bg-white shadow-2xl border border-black/5 overflow-hidden">
          {q.trim().length === 0 ? (
            <div className="p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Popular searches</p>
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => { setQ(ex); navigate(`/search?q=${encodeURIComponent(ex)}`); setOpen(false); }}
                  className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-lg hover:bg-secondary text-navy text-sm">
                  <Search size={15} className="text-gold" /> {ex}
                </button>
              ))}
            </div>
          ) : (
            <div className="py-2 max-h-96 overflow-y-auto">
              {sugs.map((s, i) => {
                const Icon = ICONS[s.type] || Search;
                return (
                  <button key={i} onClick={() => { navigate(s.url); setOpen(false); setQ(""); }}
                    className="flex items-center gap-3 w-full text-left px-4 py-3 hover:bg-secondary">
                    <span className="w-8 h-8 rounded-lg bg-navy/5 flex items-center justify-center text-navy shrink-0"><Icon size={16} /></span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-navy truncate">{s.label}</span>
                      <span className="block text-xs text-muted-foreground">{s.type} · {s.sub}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
